import type { PostHog } from "posthog-js";

/**
 * Ask "did you apply? track it" the moment a signed-in seeker comes back from
 * the employer's tab, not minutes later (2026-10-30).
 *
 * Evidence: scripts/research/aha-moment-2026-10/README.md, Round 1b. In the
 * first three days, the apply-tracking loop (marking a job Applied, answering
 * the apply-confirm prompt, using the tracker) showed up in 6/18 retained
 * sessions vs 1/11 churned; `apply_confirm_answered` fired for 5/10 retained
 * persons vs 0/10 churned. Applying in the first session barely separated the
 * groups (5/9 vs 3/9). Tracking, not applying, is the habit that stuck.
 *
 * What already exists, and what this changes. `ConfirmApplyToast` already asks
 * on tab return, but it waits `CONFIRM_MIN_DELAY_MS` (3 min) after the
 * outbound hop and stays hidden on any return before that. PostHog 14 d to
 * 2026-10-30, signed-in: 57 apply-clickers, 36 saw the prompt (median 6 min
 * after the hop), 9 answered "applied". The `treatment` arm drops the wait to
 * `POST_APPLY_TRACK_MIN_AWAY_MS` and renders a more prominent card in the same
 * toast slot with three actions: track it / not yet / remind me. Same API
 * (`/api/apply/confirm`, `/api/apply/remind`), same one-prompt-per-job claims.
 *
 * Signed-in only. `aha-signup-ask` targets anonymous visitors after an apply
 * click; the two never meet on one user at one time.
 *
 * `control`, an unresolved read and any unknown value keep today's toast
 * exactly. PostHog flag "post-apply-track-prompt" (multivariate
 * control/treatment, 50/50) is the SOLE switch. It is read client-side at the
 * trigger (a signed-in tab return with a fresh pending hop), so
 * `$feature_flag_called` means "a signed-in seeker came back after applying".
 */
export const POST_APPLY_TRACK_PROMPT_FLAG = "post-apply-track-prompt";

/** Shortest time away from the tab before the treatment card may show,
 * measured from the outbound hop. Long enough that a mis-click straight back
 * is not asked; short enough to beat the 3 min control wait. */
export const POST_APPLY_TRACK_MIN_AWAY_MS = 10_000;

export const POST_APPLY_TRACK_PROMPT_SHOWN_EVENT =
	"post_apply_track_prompt_shown";
export const POST_APPLY_TRACK_PROMPT_ANSWERED_EVENT =
	"post_apply_track_prompt_answered";
/** `surface` sent to `/api/apply/confirm`, echoed on `apply_confirm_answered`. */
export const POST_APPLY_RETURN_SURFACE = "post_apply_return";

export type PostApplyTrackPromptArm = "control" | "treatment";
export type PostApplyTrackPromptAnswer =
	| "applied"
	| "not_yet"
	| "remind"
	| "dismiss";

/**
 * Only the exact strings "control" / "treatment" are arms. `undefined` (not
 * loaded), `false` (off / no match), `true` and unknown strings return null.
 */
export function resolvePostApplyTrackPromptArm(
	raw: unknown,
): PostApplyTrackPromptArm | null {
	return raw === "control" || raw === "treatment" ? raw : null;
}

/**
 * Non-production e2e override, same contract as the `aha-signup-ask` one: the
 * `ld_preview_force` cookie with a `post-apply-track-prompt=<arm>` token is
 * honoured only when posthog-js is NOT loaded (production always loads it)
 * and the host is not lokerdollar.com or a subdomain. It can only pick an arm
 * for the browser that set the cookie.
 */
export function readPostApplyTrackPromptE2eOverride(
	posthogLoaded: boolean,
): PostApplyTrackPromptArm | null {
	if (posthogLoaded) return null;
	try {
		const host = window.location.hostname;
		if (host === "lokerdollar.com" || host.endsWith(".lokerdollar.com")) {
			return null;
		}
		const raw = document.cookie
			.split(";")
			.map((c) => c.trim())
			.find((c) => c.startsWith("ld_preview_force="));
		if (!raw) return null;
		const tokens = decodeURIComponent(raw.slice("ld_preview_force=".length))
			.split(",")
			.map((t) => t.trim());
		for (const token of tokens) {
			const sep = token.indexOf("=");
			if (sep <= 0 || token.slice(0, sep) !== POST_APPLY_TRACK_PROMPT_FLAG) {
				continue;
			}
			return resolvePostApplyTrackPromptArm(token.slice(sep + 1));
		}
	} catch {
		/* no document / cookie access: no override */
	}
	return null;
}

type FlagReader = Pick<PostHog, "getFeatureFlag"> & { __loaded?: boolean };

/** Synchronous read at the trigger: the e2e override, else the live flag. */
export function readPostApplyTrackPromptArm(
	posthog: FlagReader | null | undefined,
): PostApplyTrackPromptArm | null {
	if (!posthog) return readPostApplyTrackPromptE2eOverride(false);
	return (
		readPostApplyTrackPromptE2eOverride(posthog.__loaded === true) ??
		resolvePostApplyTrackPromptArm(
			posthog.getFeatureFlag?.(POST_APPLY_TRACK_PROMPT_FLAG),
		)
	);
}

/** Once per job, per browser. Answering or dismissing both count. */
const SHOWN_KEY_PREFIX = "ld_post_apply_track_prompt:";

export function hasShownPostApplyTrackPrompt(jobId: string): boolean {
	try {
		return window.localStorage.getItem(SHOWN_KEY_PREFIX + jobId) !== null;
	} catch {
		return false;
	}
}

export function markPostApplyTrackPromptShown(jobId: string): void {
	try {
		window.localStorage.setItem(SHOWN_KEY_PREFIX + jobId, String(Date.now()));
	} catch {
		/* storage blocked: the confirm claims still stop a second ask this page */
	}
}

export type TrackPromptDecision =
	| { kind: "show" }
	| { kind: "wait"; ms: number }
	/** Not this card: fall through to the toast's existing behaviour. */
	| { kind: "default" };

/**
 * Whether a pending hop gets the treatment card now, later, or not at all.
 * Pure so the routing is unit-testable. `readArm` is called only once every
 * cheaper gate has passed, so the flag read (and its `$feature_flag_called`)
 * happens only for a signed-in seeker with a fresh, not-yet-asked hop.
 */
export function decidePostApplyTrackPrompt(args: {
	signedIn: boolean;
	hop: { jobId: string; ts: number; snoozedUntil?: number };
	now: number;
	alreadyShown: boolean;
	readArm: () => PostApplyTrackPromptArm | null;
}): TrackPromptDecision {
	const { signedIn, hop, now, alreadyShown, readArm } = args;
	if (!signedIn || alreadyShown) return { kind: "default" };
	// A snoozed hop is the toast's one re-ask after "Not yet": not ours.
	if (typeof hop.snoozedUntil === "number") return { kind: "default" };
	if (readArm() !== "treatment") return { kind: "default" };
	const wait = hop.ts + POST_APPLY_TRACK_MIN_AWAY_MS - now;
	return wait > 0 ? { kind: "wait", ms: wait } : { kind: "show" };
}
